/**
 * CSV imports list — `/catalog/imports` (SPEC screen 7). Uncommitted imports
 * carry a discard action (safe re-upload path: nothing ever half-commits
 * from an abandoned import).
 */
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, toast } from "@erp/ui";

import { DataTable, badgeCell, dateTimeCell } from "@/components/data-table";
import type { BadgeCellVariant } from "@/components/data-table";

import { discardProductImport, type ProductImport } from "../api";
import { catalogKeys, useImportsList } from "../queries";

const STATUS_VARIANTS: Record<string, BadgeCellVariant> = {
  uploaded: "outline",
  validating: "outline",
  validated: "secondary",
  committing: "warning",
  committed: "success",
  failed: "destructive",
  discarded: "destructive",
};

/** Everything not yet (or no longer) committing/committed can be discarded. */
const DISCARDABLE_STATUSES = new Set(["uploaded", "validating", "validated", "failed"]);

export interface ImportsListPageProps {
  onOpenImport: (importId: string) => void;
  onNewImport: () => void;
  canCreate?: boolean;
  canDiscard?: boolean;
}

export function ImportsListPage({
  onOpenImport,
  onNewImport,
  canCreate = true,
  canDiscard = true,
}: ImportsListPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useImportsList();

  const discardMutation = useMutation({
    mutationFn: (importId: string) => discardProductImport(importId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: catalogKeys.imports.all }),
    onError: () =>
      toast({
        variant: "destructive",
        title: t("catalog.imports.discardFailed", "The import could not be discarded."),
      }),
  });

  const statusLabel = (value: string): string => t(`catalog.imports.status.${value}`, value);

  const columns = useMemo<ColumnDef<ProductImport>[]>(
    () => [
      {
        accessorKey: "filename",
        id: "filename",
        header: t("catalog.imports.columns.filename", "File"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("catalog.imports.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(value, STATUS_VARIANTS, statusLabel(value));
        },
      },
      {
        accessorKey: "total_rows",
        id: "total_rows",
        header: t("catalog.imports.columns.rows", "Rows"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        accessorKey: "error_count",
        id: "error_count",
        header: t("catalog.imports.columns.errors", "Errors"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        accessorKey: "created_at",
        id: "created_at",
        header: t("catalog.imports.columns.created", "Created"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: (ctx) => {
          const row = ctx.row.original;
          const discardable = DISCARDABLE_STATUSES.has(row.status);
          return discardable && canDiscard ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={discardMutation.isPending}
              onClick={(event) => {
                event.stopPropagation();
                discardMutation.mutate(row.id);
              }}
            >
              {t("catalog.imports.discard", "Discard")}
            </Button>
          ) : null;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- label helper derives from t
    [t, discardMutation.isPending, canDiscard],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t("catalog.imports.title", "Product CSV imports")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "catalog.imports.subtitle",
              "Two-phase: upload and validate first, commit clean rows second.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button onClick={onNewImport}>{t("catalog.imports.new", "New import")}</Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenImport(row.id)}
        showColumnsMenu={false}
        emptyMessage={t(
          "catalog.imports.empty",
          "No imports yet — upload a CSV to stage products.",
        )}
        errorMessage={t("catalog.imports.loadError", "Imports could not be loaded.")}
      />
    </div>
  );
}
